import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  Delete as DeleteIcon,
  Calculate as SolveIcon,
  Close as CloseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Science as CalibratedIcon,
} from '@mui/icons-material';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { buildQualityRows } from '../../utils/qualityMetrics';
import axios from 'axios';
import { formatSize, filePreviewUrl } from '../../utils/fileUtils';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import FileBreadcrumb from '../../components/files/FileBreadcrumb';
import { SolveChip, isCalibrationFrame } from '../../components/files/FileGrid';
import ProcessingFailureChip from '../../components/files/ProcessingFailureChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getAccessToken } from '../../services/authStorage';

const API_BASE_URL = '/api/v1';

function formatUTC(dateString, formatStr = 'PPP HH:mm:ss') {
  if (!dateString) return 'N/A';
  try {
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      utcDateString = dateString + 'Z';
    }
    return formatInTimeZone(parseISO(utcDateString), 'UTC', formatStr) + ' UTC';
  } catch (error) {
    return dateString;
  }
}

function decimalToSexagesimal(decimal, isRA = false) {
  if (decimal === null || decimal === undefined) return 'N/A';
  const absolute = Math.abs(decimal);
  if (isRA) {
    const hours = absolute / 15;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(2).padStart(5, '0')}s`;
  } else {
    const sign = decimal < 0 ? '-' : '+';
    const d = Math.floor(absolute);
    const m = Math.floor((absolute - d) * 60);
    const s = ((absolute - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}° ${m.toString().padStart(2, '0')}' ${s.toFixed(1).padStart(4, '0')}"`;
  }
}

const FileDetail = () => {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const { getDownloadUrl, downloadCalibrated } = useFileDownload();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [calibrateError, setCalibrateError] = useState(null);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: null });
  const [hasMatchingMasters, setHasMatchingMasters] = useState(false);
  const [matchingMasters, setMatchingMasters] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchFile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { file_id: fileId }
      });
      if (response.data.success) {
        const files = response.data.data?.files || [];
        if (files.length > 0) {
          setFile(files[0]);
          setHasMatchingMasters(response.data.data?.has_matching_masters || false);
          setMatchingMasters(response.data.data?.matching_masters || null);
        } else {
          setError('File not found');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load file details');
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => {
    fetchFile();
  }, [fetchFile]);

  // WebSocket for live plate-solve updates
  const handleFileUpdate = useCallback((update) => {
    if (update.file_id === fileId) {
      setFile(prev => prev ? {
        ...prev,
        plate_solve_status: update.plate_solve_status,
        plate_solve_result: update.plate_solve_result || prev.plate_solve_result
      } : prev);
    }
  }, [fileId]);

  useFileWebSocket(handleFileUpdate);

  const handleSolve = async () => {
    try {
      const token = getAccessToken();
      await axios.post(
        `${API_BASE_URL}/user-files/${fileId}/plate-solve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      alert('Failed to queue plate solving job');
    }
  };

  const handleDelete = () => {
    const filename = file?.filename || file?.file_name || 'this file';
    setConfirm({
      title: 'Delete File',
      message: <span>Are you sure you want to delete <strong>{filename}</strong>?</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const token = getAccessToken();
          await axios.delete(`${API_BASE_URL}/user-files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          navigate(-1);
        } catch (err) {
          setCalibrateError('Failed to delete file');
          throw err;
        }
      }
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
        </Box>
      </Container>
    );
  }

  if (!file) return null;

  const fileName = file.file_name || file.filename || 'Unknown';
  const targetName = file.target_name || file.fits_header?.OBJECT;
  const previewUrl = filePreviewUrl(file);

  const breadcrumbItems = [
    { label: 'Files', path: '/files' },
  ];
  if (targetName) {
    breadcrumbItems.push({ label: targetName, path: `/files/target/${encodeURIComponent(targetName)}` });
  }
  breadcrumbItems.push({ label: fileName });

  const stats = file.quality_metrics;
  const header = file.fits_header;

  // Read directly from UserFile fields
  const filter = file.filter;
  const exposureTime = file.exposure_time;
  const binning = file.binning || (header?.XBINNING ? `${header.XBINNING}x${header.YBINNING || header.XBINNING}` : null);
  const imageType = file.image_type || header?.IMAGETYP || file.file_type;
  const gain = file.gain ?? header?.GAIN;
  // #1531: calibration frames (bias/dark/flat/offset) are not themselves calibratable —
  // never offer "Download Calibrated" / masters UI for them. Resolve image_type the same way
  // the server guard does (image_type, then FITS IMAGETYP) so the two agree — NOT file_type
  // (an enum like 'raw'/'calibrated', not a frame type).
  const isCalibration = isCalibrationFrame({ image_type: file.image_type || header?.IMAGETYP });

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <FileBreadcrumb items={breadcrumbItems} />

        <Typography variant="h5" gutterBottom>{fileName}</Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ButtonGroup variant="contained" color="primary">
            <Button
              startIcon={<DownloadIcon />}
              component="a"
              href={getDownloadUrl(fileId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </Button>
            <Button
              size="small"
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
          >
            <MenuItem
              component="a"
              href={getDownloadUrl(fileId)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setDownloadMenuAnchor(null)}
            >
              <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
              Download Raw
            </MenuItem>
            {isCalibration ? null : hasMatchingMasters ? (
              <MenuItem
                onClick={async () => {
                  setDownloadMenuAnchor(null);
                  const result = await downloadCalibrated(fileId);
                  if (!result.success) {
                    setCalibrateError(result.error);
                  }
                }}
              >
                <CalibratedIcon sx={{ mr: 1 }} fontSize="small" />
                Download Calibrated
              </MenuItem>
            ) : (
              <Tooltip title="No matching calibration masters available" placement="right">
                <span>
                  <MenuItem disabled>
                    <CalibratedIcon sx={{ mr: 1 }} fontSize="small" />
                    Download Calibrated
                  </MenuItem>
                </span>
              </Tooltip>
            )}
          </Menu>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SolveIcon />}
            onClick={handleSolve}
          >
            Plate Solve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>

        {/* Preview Image */}
        {previewUrl && (
          <Paper sx={{ mb: 3, p: 2, bgcolor: '#0a0a0a', textAlign: 'center', position: 'relative' }}>
            <Box
              component="img"
              src={previewUrl}
              alt={fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: 500,
                objectFit: 'contain',
                cursor: 'pointer',
                borderRadius: 1,
              }}
              onClick={() => setPreviewDialog({ open: true, url: previewUrl })}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Chip
              label={file.preview_calibrated ? 'Calibrated Preview' : 'Uncalibrated Preview'}
              size="small"
              color={file.preview_calibrated ? 'success' : 'default'}
              variant={file.preview_calibrated ? 'filled' : 'outlined'}
              sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.9 }}
            />
          </Paper>
        )}

        {/* Basic Information */}
        <Typography variant="h6" gutterBottom>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold', width: '30%' }}>Filename</TableCell>
                <TableCell>{fileName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>File Size</TableCell>
                <TableCell>{formatSize(file.file_size || file.file_size_bytes)}</TableCell>
              </TableRow>
              {(targetName || header?.OBJECT) && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Object / Target</TableCell>
                  <TableCell>{header?.OBJECT || targetName}</TableCell>
                </TableRow>
              )}
              {filter && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Filter</TableCell>
                  <TableCell><Chip label={filter} size="small" color="primary" /></TableCell>
                </TableRow>
              )}
              {exposureTime && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Exposure Time</TableCell>
                  <TableCell>{exposureTime} seconds</TableCell>
                </TableRow>
              )}
              {gain != null && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Gain</TableCell>
                  <TableCell>{gain}</TableCell>
                </TableRow>
              )}
              {binning && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Binning</TableCell>
                  <TableCell>{binning}</TableCell>
                </TableRow>
              )}
              {imageType && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Image Type</TableCell>
                  <TableCell>{imageType}</TableCell>
                </TableRow>
              )}
              {file.is_compressed && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Compression</TableCell>
                  <TableCell>{file.compression_type ? file.compression_type.toUpperCase() : 'Yes'}</TableCell>
                </TableRow>
              )}
              {(file.target_ra != null || header?.RA != null) && (file.target_dec != null || header?.DEC != null) && (
                <>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Coordinates (RA/Dec)</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {decimalToSexagesimal(file.target_ra ?? header?.RA, true)} / {decimalToSexagesimal(file.target_dec ?? header?.DEC, false)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>Coordinates (Decimal)</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                      {(file.target_ra ?? header?.RA)?.toFixed(6)}&deg; / {(file.target_dec ?? header?.DEC)?.toFixed(6)}&deg;
                    </TableCell>
                  </TableRow>
                </>
              )}
              {header?.['DATE-OBS'] && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Observation Date</TableCell>
                  <TableCell>{formatUTC(header['DATE-OBS'])}</TableCell>
                </TableRow>
              )}
              {header?.AIRMASS && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Airmass</TableCell>
                  <TableCell>{Number(header.AIRMASS).toFixed(3)}</TableCell>
                </TableRow>
              )}
              {header?.TELESCOP && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Telescope</TableCell>
                  <TableCell>{header.TELESCOP}</TableCell>
                </TableRow>
              )}
              {(file.camera_name || header?.INSTRUME) && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Instrument</TableCell>
                  <TableCell>{file.camera_name || header?.INSTRUME}</TableCell>
                </TableRow>
              )}
              {header?.OBSERVER && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Observer</TableCell>
                  <TableCell>{header.OBSERVER}</TableCell>
                </TableRow>
              )}
              {file.observation_name && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Observation</TableCell>
                  <TableCell>{file.observation_name}</TableCell>
                </TableRow>
              )}
              {file.observatory_name && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Observatory</TableCell>
                  <TableCell>{file.observatory_name}</TableCell>
                </TableRow>
              )}
              {/* Plate Solve Status */}
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Plate Solve</TableCell>
                <TableCell>
                  <SolveChip file={file} />
                </TableCell>
              </TableRow>
              {/* Processing failure reason (#1206) — before this, a failed file
                  showed nothing here and the cause lived only on the job row. */}
              {file.processing_status === 'failed' && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Processing</TableCell>
                  <TableCell>
                    <ProcessingFailureChip file={file} />
                    {file.processing_error && (
                      <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.85em', color: 'text.secondary', wordBreak: 'break-word' }}>
                        {file.processing_error}
                      </Box>
                    )}
                    {file.processing_failed_at && (
                      <Box sx={{ mt: 0.5, fontSize: '0.85em', color: 'text.secondary' }}>
                        {formatUTC(file.processing_failed_at)}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
              {/* Calibration Status — light frames only; calibration frames aren't calibratable (#1531) */}
              {!isCalibration && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Calibration Masters</TableCell>
                  <TableCell>
                    {hasMatchingMasters ? (
                      <Chip label="Masters Available" size="small" color="success" />
                    ) : (
                      <Chip label="No Matching Masters" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              )}
              {file.checksum && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Checksum</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{file.checksum}</TableCell>
                </TableRow>
              )}
              {file.uploaded_at && (
                <TableRow>
                  <TableCell component="th" sx={{ fontWeight: 'bold' }}>Uploaded</TableCell>
                  <TableCell>{formatUTC(file.uploaded_at)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Image Statistics */}
        {stats && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Image Statistics</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {buildQualityRows(stats).map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%' }}>{label}</TableCell>
                      <TableCell>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* FITS Header Data */}
        {header && Object.keys(header).length > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>FITS Header Data</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableBody>
                  {Object.entries(header)
                    .filter(([key]) => {
                      const skipKeys = ['FILTER', 'EXPTIME', 'EXPOSURE', 'OBJECT', 'INSTRUME',
                        'TELESCOP', 'XBINNING', 'YBINNING', 'RA', 'DEC', 'AIRMASS'];
                      return !skipKeys.includes(key.toUpperCase());
                    })
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell component="th" sx={{ fontWeight: 'bold', width: '30%', fontFamily: 'monospace' }}>
                          {key}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                          {typeof value === 'number' ? value.toFixed(6).replace(/\.?0+$/, '') : String(value)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Matching Masters */}
        {!isCalibration && matchingMasters && Object.keys(matchingMasters).length > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Matching Calibration Masters</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  {matchingMasters.dark && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold', width: '30%' }}>Dark Master</TableCell>
                      <TableCell>
                        {matchingMasters.dark.temperature != null && `${matchingMasters.dark.temperature}°C`}
                        {matchingMasters.dark.captured_at &&
                          ` — ${new Date(matchingMasters.dark.captured_at).toLocaleDateString()}`}
                        <Button
                          size="small"
                          sx={{ ml: 1 }}
                          component="a"
                          href={`/api/v1/calibration/frames/${matchingMasters.dark.id}/download`}
                          target="_blank"
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {matchingMasters.flat && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Flat Master</TableCell>
                      <TableCell>
                        {matchingMasters.flat.filter && `${matchingMasters.flat.filter} `}
                        {matchingMasters.flat.captured_at &&
                          `— ${new Date(matchingMasters.flat.captured_at).toLocaleDateString()}`}
                        <Button
                          size="small"
                          sx={{ ml: 1 }}
                          component="a"
                          href={`/api/v1/calibration/frames/${matchingMasters.flat.id}/download`}
                          target="_blank"
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {matchingMasters.bias && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Bias Master</TableCell>
                      <TableCell>
                        {matchingMasters.bias.captured_at &&
                          new Date(matchingMasters.bias.captured_at).toLocaleDateString()}
                        <Button
                          size="small"
                          sx={{ ml: 1 }}
                          component="a"
                          href={`/api/v1/calibration/frames/${matchingMasters.bias.id}/download`}
                          target="_blank"
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Storage Information */}
        {file.file_path && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Storage Information</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold', width: '30%' }}>Storage Path</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{file.file_path}</TableCell>
                  </TableRow>
                  {file.bucket && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Bucket</TableCell>
                      <TableCell>{file.bucket}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      {/* Full-size Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, url: null })}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>{fileName}</Typography>
          <IconButton onClick={() => setPreviewDialog({ open: false, url: null })} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{
          p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center',
          bgcolor: '#0a0a0a', minHeight: '400px', maxHeight: '85vh', overflow: 'auto'
        }}>
          {previewDialog.url && (
            <img
              src={previewDialog.url}
              alt={fileName}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="padding: 40px; color: #999;">Failed to load image</div>';
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(calibrateError)}
        autoHideDuration={8000}
        onClose={() => setCalibrateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCalibrateError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {calibrateError}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Container>
  );
};

export default FileDetail;
